import { ApolloError, useQuery } from '@apollo/client';
import { DownloadOutlined } from '@ant-design/icons';
import { Alert, Button, Drawer, message } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { translateGraphQLError } from '../../../../utils/graphqlUtils';
import { ENABLE_DOWNLOADS, FREEZE_HOURS } from '../constants';
import {
    GET_SORTING_BATCH_BAGS,
    GET_SORTING_BATCH_OPERATION_LOGS,
    GetSortingBatchBagsResponse,
    GetSortingBatchOperationLogsResponse,
    SortingBatchDetailVariables,
    toBatchBag,
    toBatchLog,
} from '../graphql/sortingBatchDetail';
import { BatchBag, BatchLog, SortingBatch } from '../types';
import { canEndSorting } from '../utils/batchRules';
import { destinationTypeOf, formatBatchTime, formatCount } from '../utils/formatUtils';
import { BatchBagSummary, BatchBagTable } from './BatchBagTable';
import { BatchOperationTimeline } from './BatchOperationTimeline';
import { BatchStatusTag, FrozenTag } from './BatchTags';
import { BatchDetailDrawerProps } from './overlayProps';

const NS = 'wms.sortingBatch';

/** 详情查询失败提示的 message key：包牌、操作历史两个查询共用，避免重复弹 */
const DETAIL_ERROR_MESSAGE_KEY = 'sortingBatchDetailError';

/** 尾程商（原型固定值） */
const LAST_MILE_CARRIER = 'SwiftX';

const FieldRow: React.FC<{ label: React.ReactNode; children: React.ReactNode; valueClassName?: string }> = ({ label, children, valueClassName }) => (
    <div className="mb-3 flex items-center gap-4">
        <span className="w-[110px] flex-none text-[13px] text-[#86909c]">{label}</span>
        <span className={`min-w-0 flex-1 text-[13px] font-medium ${valueClassName || 'text-[#1d2129]'}`}>{children}</span>
    </div>
);

interface BatchDetailData {
    bags: BatchBag[];
    summary: BatchBagSummary;
    /** 包牌查询还没返回或失败时为空，展示占位符而不是 0 */
    parcels?: number;
    packedBags?: number;
    logs: BatchLog[];
    bagsLoading: boolean;
    logsLoading: boolean;
    /** 操作历史查询失败且没有数据 */
    isLogsFailed: boolean;
}

/**
 * 详情的包牌列表与操作历史：发出、接收两个方向都查后端（sortingBatchBags / sortingBatchOperationLogs），
 * 由后端校验批次对当前仓可见（发出 = 本仓创建，接收 = 目的地是本仓）。
 */
const useBatchDetailData = (batch: SortingBatch): BatchDetailData => {
    const { currentWarehouse } = useWarehouseContext();
    const queryOptions = {
        variables: { warehouseId: currentWarehouse?.id || '0', batchId: String(batch.id) },
        skip: !currentWarehouse?.id,
        fetchPolicy: 'network-only' as const,
        // 全局 errorLink 对业务错误（如批次不存在或无权查看）、校验错误不提示，这里补上；网络、权限、系统错误，
        // 以及同为业务错误的降级熔断（errors.system.serviceUnavailable）它已提示，不再重复。两个查询同时失败时用同一个 key 只留一条
        onError: (e: ApolloError) => {
            const extensions = e.graphQLErrors?.[0]?.extensions;
            const code = extensions?.code;
            const isShownByErrorLink = !!e.networkError
                || (code !== 'BUSINESS_ERROR' && code !== 'VALIDATION_ERROR')
                || extensions?.messageKey === 'errors.system.serviceUnavailable';
            if (isShownByErrorLink) return;
            message.error({ content: translateGraphQLError(e, `${NS}.common.requestFailed`), key: DETAIL_ERROR_MESSAGE_KEY });
        },
    };
    const bagsQuery = useQuery<GetSortingBatchBagsResponse, SortingBatchDetailVariables>(GET_SORTING_BATCH_BAGS, queryOptions);
    const logsQuery = useQuery<GetSortingBatchOperationLogsResponse, SortingBatchDetailVariables>(GET_SORTING_BATCH_OPERATION_LOGS, queryOptions);

    return useMemo(() => {
        const result = bagsQuery.data?.sortingBatchBags;
        return {
            bags: (result?.bags || []).map((bag) => toBatchBag(bag, batch.batchNumber)),
            summary: { created: result?.createdBagCount ?? 0, used: result?.usedBagCount ?? 0, unused: result?.unusedBagCount ?? 0 },
            parcels: result?.parcelCount,
            packedBags: result?.usedBagCount,
            logs: (logsQuery.data?.sortingBatchOperationLogs || []).map((log) => toBatchLog(log, batch.batchNumber)),
            bagsLoading: bagsQuery.loading,
            logsLoading: logsQuery.loading,
            isLogsFailed: !logsQuery.data && !!logsQuery.error,
        };
    }, [batch, bagsQuery.data, bagsQuery.loading, logsQuery.data, logsQuery.loading, logsQuery.error]);
};

type DetailBodyProps = Omit<BatchDetailDrawerProps, 'open' | 'batch' | 'onClose' | 'onOpenEndSorting'> & {
    batch: SortingBatch;
    frozenAt?: number;
};

/** 抽屉内容：仅在抽屉打开期间挂载（destroyOnHidden），关闭后不再派生包牌列表与操作历史 */
const BatchDetailBody: React.FC<DetailBodyProps> = ({
    batch, frozenAt, direction, onOpenBagDetail, onOpenPrint, onOpenMoveBags, onOpenCreateBags,
}) => {
    const { t } = useTranslation();
    const frozenTime = formatBatchTime(frozenAt);
    const detail = useBatchDetailData(batch);
    const countText = (n?: number) => (n === undefined ? t(`${NS}.common.none`) : formatCount(n));

    const handleExport = () => {
        // todo batch mock: 待后端提供 Query 批次明细导出下载链接（batchId → url）
        message.success(t(`${NS}.detail.exported`));
    };

    return (
        <div className="flex min-h-full flex-wrap items-stretch">
            <div className="min-w-0 flex-[1_1_520px] px-[30px] pb-[30px] pt-[22px]">
                {frozenAt !== undefined && (
                    <Alert
                        className="mb-5"
                        type="warning"
                        showIcon
                        message={t(`${NS}.detail.frozenNoticeTitle`)}
                        description={t(`${NS}.detail.frozenNoticeBody`, { hours: FREEZE_HOURS, time: frozenTime })}
                    />
                )}
                <FieldRow label={t(`${NS}.detail.fields.destination`)}>{batch.destination || t(`${NS}.common.none`)}</FieldRow>
                <FieldRow label={t(`${NS}.detail.fields.destinationType`)}>
                    {t(`${NS}.destinationType.${destinationTypeOf(batch.destination)}`)}
                </FieldRow>
                <FieldRow label={t(`${NS}.detail.fields.lastMileCarrier`)}>{LAST_MILE_CARRIER}</FieldRow>
                <FieldRow label={t(`${NS}.detail.fields.createdAt`)}>{formatBatchTime(batch.createdAt)}</FieldRow>
                <FieldRow label={t(`${NS}.detail.fields.creationMethod`)}>{t(`${NS}.creationMethodFull.${batch.creationMethod}`)}</FieldRow>
                <FieldRow label={t(`${NS}.detail.fields.creator`)}>{batch.creator || t(`${NS}.common.none`)}</FieldRow>
                <FieldRow label={t(`${NS}.detail.fields.status`)}>{t(`${NS}.status.${batch.status}`)}</FieldRow>
                {frozenAt !== undefined && (
                    <FieldRow label={t(`${NS}.detail.fields.dataUpdate`)} valueClassName="text-[#d25f00]">
                        {t(`${NS}.detail.frozenAt`, { time: frozenTime })}
                    </FieldRow>
                )}

                <div className="mb-3 mt-[26px] flex flex-wrap items-center gap-[18px]">
                    <span className="text-[13px] text-[#4e5969]">
                        {t(`${NS}.detail.parcels`)}
                        {' '}
                        <b className="text-sm font-medium text-[#1d2129]">{countText(detail.parcels)}</b>
                    </span>
                    <span className="text-[13px] text-[#4e5969]">
                        {t(`${NS}.detail.packedBags`)}
                        {' '}
                        <b className="text-sm font-medium text-[#1d2129]">{countText(detail.packedBags)}</b>
                    </span>
                    <div className="flex-1" />
                    {/* 下载延期（2026-10-14，plan §4.10，是否必须保留待评估）：ENABLE_DOWNLOADS 为 false 时隐藏，代码保留 */}
                    {ENABLE_DOWNLOADS && (
                        <Button type="text" size="small" icon={<DownloadOutlined />} onClick={handleExport}>
                            {t(`${NS}.detail.export`)}
                        </Button>
                    )}
                </div>

                <BatchBagTable
                    batch={batch}
                    bags={detail.bags}
                    summary={detail.summary}
                    loading={detail.bagsLoading}
                    direction={direction}
                    onOpenBagDetail={onOpenBagDetail}
                    onOpenPrint={onOpenPrint}
                    onOpenMoveBags={onOpenMoveBags}
                    onOpenCreateBags={onOpenCreateBags}
                />
            </div>
            <div className="min-w-0 max-w-[360px] flex-[1_1_300px] border-0 border-l border-solid border-[#f2f3f5] px-[26px] pb-[30px] pt-[22px]">
                <BatchOperationTimeline logs={detail.logs} loading={detail.logsLoading} isFailed={detail.isLogsFailed} />
            </div>
        </div>
    );
};

/** 批次详情抽屉（原型界面 ②）：基本信息、包牌列表、操作历史；接收方向只读（不可移动 / 创建包牌 / 结束分拣） */
export const BatchDetailDrawer: React.FC<BatchDetailDrawerProps> = ({
    open, batch, direction, onClose, onOpenBagDetail, onOpenPrint, onOpenMoveBags, onOpenCreateBags, onOpenEndSorting,
}) => {
    const { t } = useTranslation();
    const outbound = direction === 'OUTBOUND';
    // 停更判定直接用列表行的 frozenAt，与列表一致
    const frozenAt = batch?.frozenAt;

    const title = batch ? (
        <div className="flex flex-wrap items-center gap-3">
            <span className="inline-block h-4 w-[3px] bg-[#722ed1]" />
            <span className="text-[17px] font-medium">{batch.batchNumber}</span>
            <BatchStatusTag status={batch.status} />
            <FrozenTag frozenAt={frozenAt} />
        </div>
    ) : null;

    const footer = batch ? (
        <div className="flex items-center gap-2.5">
            {outbound && (
                <Button
                    color="orange"
                    variant="filled"
                    disabled={!canEndSorting(batch.status)}
                    onClick={() => onOpenEndSorting([{ id: batch.id, batchNumber: batch.batchNumber, status: batch.status }])}
                >
                    {t(`${NS}.detail.endSorting`)}
                </Button>
            )}
            <div className="flex-1" />
            <Button color="primary" variant="filled" className="min-w-[88px]" onClick={onClose}>
                {t(`${NS}.common.ok`)}
            </Button>
        </div>
    ) : null;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width="min(1240px, 94vw)"
            destroyOnHidden
            title={title}
            footer={footer}
            styles={{ body: { padding: 0 } }}
        >
            {batch && (
                <BatchDetailBody
                    key={batch.id}
                    batch={batch}
                    frozenAt={frozenAt}
                    direction={direction}
                    onOpenBagDetail={onOpenBagDetail}
                    onOpenPrint={onOpenPrint}
                    onOpenMoveBags={onOpenMoveBags}
                    onOpenCreateBags={onOpenCreateBags}
                />
            )}
        </Drawer>
    );
};
